import { useEffect, useMemo, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { Body1, Caption1 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { isTickmillPartner } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'

import { SelectField } from '@global/field/SelectField'

import { useIBPrograms } from '@hooks/useIBPrograms'
import { useIBReferralCodes } from '@hooks/useIBReferralCodes'
import { ScrollToIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'
import { HowIBWorksModal } from '@shared/modals/HowIBWorks.modal'

import { IBSignUpTypePage } from '../../IntroducingBroker/SignUp/Type/IBSignUpTypePage'
import { useIntroducingBrokerById } from '../../IntroducingBroker/hooks/useIntroducingBrokerById'

import styles from './IntroducingBroker.module.scss'

export const IntroducingBrokerPage = (): JSX.Element => {
  useScrollToTop()
  const { account } = useAccountReadContext()
  const isPartner = isTickmillPartner(account)
  const { defaultProgram, multiTierProgram } = useIBPrograms()
  const howIBWorksModal = useModal(HowIBWorksModal)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: introducingBroker } = useIntroducingBrokerById()

  const { referralCodes, ibCode, mtCode, isLoading } = useIBReferralCodes()
  const [selectedCode, setSelectedCode] = useState(ibCode || mtCode)
  const isMobile = useWindowResize()

  useEffect(() => setSelectedCode(ibCode || mtCode), [ibCode, mtCode])
  const canRegister = useMemo(
    () => ((multiTierProgram && !mtCode) || (defaultProgram && !ibCode)) ?? true,
    [ibCode, mtCode, multiTierProgram, defaultProgram]
  )

  return (
    <Loading showLoadingIcon isLoading={isLoading}>
      {!isMobile && (
        <PageSection title={t('IB.Introducing Broker Program')} id={ScrollToIds.IBProfileHeader} />
      )}
      <div className={styles.container}>
        {!isPartner && (
          <section className={styles.inviteSection}>
            <Body1>
              {t(
                'IB.Invite traders to join Tickmill by using your personal link and get a permanent commission on every trade they make.'
              )}
            </Body1>
            <SelectField
              options={referralCodes.map(({ code }) => ({
                value: code,
                label: code,
              }))}
              allowCopy
              onChange={(v) => setSelectedCode(v.target.value)}
              value={selectedCode}
              disabled={referralCodes.length < 2}
              label={t('IB.Your Referral Code')}
            />

            {selectedCode === ibCode && (
              <div className={styles.link} onClick={() => howIBWorksModal.open({ ibCode })}>
                {t('IB.How IB works?')}
              </div>
            )}
            {selectedCode === mtCode && (
              <div
                className={styles.link}
                onClick={() =>
                  howIBWorksModal.open({
                    isMt: true,
                    ibCode,
                  })
                }
              >
                {t('IB.MultiTier.How Multi-tier works')}?
              </div>
            )}
          </section>
        )}
        <section className={styles.linkSection}>
          <div onClick={() => navigate('/dashboard/introducing-broker/legal-documents')}>
            <Body1 medium className={styles.documents}>
              {t('Profile.Legal Documents')}{' '}
              <span className='icon is-right has-cursor-pointer'>
                <ChevronIcon.Right size={12} />
              </span>
            </Body1>
            <Caption1 isParagraph>{t('Profile.All the legal documents')}</Caption1>
          </div>
          {introducingBroker?.loyaltyProgramEnabled && (
            <div onClick={() => navigate('/dashboard/introducing-broker/bonus-program')}>
              <Body1 medium className={styles.documents}>
                {t('Profile.Loyalty Program')}{' '}
                <span className='icon is-right has-cursor-pointer'>
                  <ChevronIcon.Right size={12} />
                </span>
              </Body1>
              <Caption1 isParagraph>
                {t('Level')}&nbsp;{introducingBroker?.loyaltyDetails.currentTier?.name}
                &nbsp;(
                {introducingBroker?.loyaltyDetails.loyaltyLots}&nbsp;{t('Profile.points')})
              </Caption1>
            </div>
          )}
          {!isPartner && (
            <div onClick={() => navigate('/dashboard/introducing-broker/referral-materials')}>
              <Body1 medium className={styles.documents}>
                {t('Profile.Promo Materials')}{' '}
                <span className='icon is-right has-cursor-pointer'>
                  <ChevronIcon.Right size={12} />
                </span>
              </Body1>
              <Caption1 isParagraph>{t('Profile.Banners, logos')}</Caption1>
            </div>
          )}
        </section>
      </div>
      {canRegister && (
        <div className={styles.signupContainer}>
          <IBSignUpTypePage isProfile />
        </div>
      )}
    </Loading>
  )
}
